import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import CertifiedBadge from '../components/CertifiedBadge'
import './Page.css'
import './FoundersWall.css'
import './Welcome.css'

const WELCOME_PREFIX = 'Welcome to '
const WELCOME_ACCENT = 'Alchemy Forum'
const WELCOME_LINE = WELCOME_PREFIX + WELCOME_ACCENT
const TYPEWRITER_CHAR_MS = 52

function WelcomeTypewriterLine({ onComplete }) {
  const [charCount, setCharCount] = useState(0)
  const [cursorVisible, setCursorVisible] = useState(true)
  const completedRef = useRef(false)
  const isComplete = charCount >= WELCOME_LINE.length
  const visible = WELCOME_LINE.slice(0, charCount)
  const prefixVisible = visible.slice(0, Math.min(visible.length, WELCOME_PREFIX.length))
  const accentVisible = visible.length > WELCOME_PREFIX.length ? visible.slice(WELCOME_PREFIX.length) : ''

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setCharCount(WELCOME_LINE.length)
      if (!completedRef.current) {
        completedRef.current = true
        onComplete?.()
      }
      return undefined
    }

    if (isComplete) {
      if (!completedRef.current) {
        completedRef.current = true
        onComplete?.()
      }
      return undefined
    }

    const timeout = window.setTimeout(() => {
      setCharCount((count) => count + 1)
    }, TYPEWRITER_CHAR_MS)

    return () => window.clearTimeout(timeout)
  }, [charCount, isComplete, onComplete])

  useEffect(() => {
    if (isComplete) {
      return undefined
    }

    const interval = window.setInterval(() => {
      setCursorVisible((visible) => !visible)
    }, 530)

    return () => window.clearInterval(interval)
  }, [isComplete])

  return (
    <span className="welcome-hero-title-line welcome-hero-typewriter" aria-hidden="true">
      {prefixVisible}
      {accentVisible ? <span className="welcome-hero-title-accent">{accentVisible}</span> : null}
      {!isComplete ? (
        <span className={`welcome-hero-typewriter-cursor${cursorVisible ? ' is-visible' : ''}`}>|</span>
      ) : null}
    </span>
  )
}

function WelcomeGlobe() {
  return (
    <div className="welcome-hero-globe-container" aria-hidden="true">
      <svg className="welcome-hero-globe" viewBox="0 0 300 300" preserveAspectRatio="xMidYMid meet">
        <circle cx="150" cy="150" r="110" fill="none" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" />
        <ellipse cx="150" cy="150" rx="110" ry="55" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <ellipse cx="150" cy="120" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <ellipse cx="150" cy="180" rx="95" ry="48" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <path d="M 150 40 Q 150 150, 150 260" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <path d="M 40 150 Q 150 150, 260 150" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <path d="M 75 60 Q 150 150, 225 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <path d="M 225 60 Q 150 150, 75 240" fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.22" />
        <circle cx="110" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite" />
        </circle>
        <circle cx="190" cy="110" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="110" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="190" cy="190" r="3" fill="var(--accent)" opacity="0.6" className="globe-node">
          <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="150" cy="150" r="5" fill="var(--accent)" opacity="0.8" className="globe-node hub">
          <animate attributeName="opacity" values="0.6;0.9;0.6" dur="2s" repeatCount="indefinite" />
          <animate attributeName="r" values="4;6;4" dur="2s" repeatCount="indefinite" />
        </circle>
        <line x1="150" y1="150" x2="110" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
          <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.5s" repeatCount="indefinite" />
        </line>
        <line x1="150" y1="150" x2="190" y2="110" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
          <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.7s" repeatCount="indefinite" />
        </line>
        <line x1="150" y1="150" x2="110" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
          <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.6s" repeatCount="indefinite" />
        </line>
        <line x1="150" y1="150" x2="190" y2="190" stroke="var(--accent)" strokeWidth="1.5" opacity="0.3" className="connection-line">
          <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2.4s" repeatCount="indefinite" />
        </line>
      </svg>
    </div>
  )
}

function Welcome() {
  const [welcomeTyped, setWelcomeTyped] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  return (
    <div className="page-container welcome-page">
      <section className="welcome-hero" aria-labelledby="welcome-hero-heading">
        <div className="welcome-hero-panel">
          <div className="welcome-hero-inner">
            <div className="welcome-hero-copy">
              <h1
                id="welcome-hero-heading"
                className="welcome-hero-title"
                aria-label="Welcome to Alchemy Forum. Fix it, trace it, photograph it, or just remember it. It all belongs here."
              >
                <WelcomeTypewriterLine onComplete={() => setWelcomeTyped(true)} />
                <span
                  className={`welcome-hero-title-body welcome-hero-title-tagline${
                    welcomeTyped ? ' is-visible' : ''
                  }`}
                >
                  Fix it, trace it, photograph it, or just remember it. It all belongs here.
                </span>
              </h1>
            </div>
            <WelcomeGlobe />
          </div>
        </div>
      </section>

      <div className="welcome-columns">
        <section className="welcome-column" aria-labelledby="welcome-problem-heading">
          <h2 id="welcome-problem-heading" className="welcome-section-title">
            The problem
          </h2>
          <p className="welcome-problem-lead">
            <strong>
              The electronics documentation community is fundamentally broken and needs a home.
            </strong>
          </p>
          <ul className="welcome-list">
            <li>Valuable repair work disappears in forum threads</li>
            <li>Images break, links die, files get buried</li>
            <li>Reverse-engineered schematics rarely have a permanent home</li>
            <li>Collaboration is fragmented</li>
          </ul>
        </section>

        <section className="welcome-column" aria-labelledby="welcome-solution-heading">
          <h2 id="welcome-solution-heading" className="welcome-section-title">
            The solution
          </h2>
          <p className="welcome-solution-lead">
            <strong>
              Alchemy Forum brings repo-style structure to circuit documentation.
            </strong>
          </p>
          <ul className="welcome-list">
            <li>create projects within open source repositories</li>
            <li>maintain records over time</li>
            <li>collaborate with others</li>
            <li>roll back revisions and fork projects</li>
            <li>preserve contributor credit</li>
          </ul>
        </section>
      </div>

      <section className="welcome-where-start" aria-labelledby="welcome-where-heading">
        <h2 id="welcome-where-heading" className="welcome-section-title">
          Where to start
        </h2>

        <ul className="welcome-next-steps">
          <li>
            <strong className="welcome-next-steps-heading">step 1</strong>
            <span className="welcome-next-steps-body">claim your name and contributor profile</span>
            <Link to="/" className="btn btn-primary welcome-next-steps-btn">
              Open an account
            </Link>
          </li>
          <li>
            <strong className="welcome-next-steps-heading">step 2</strong>
            <span className="welcome-next-steps-body">check out the activity happening on the site</span>
            <Link to="/repositories" className="btn btn-primary welcome-next-steps-btn">
              Repositories
            </Link>
          </li>
          <li>
            <strong className="welcome-next-steps-heading">step 3</strong>
            <span className="welcome-next-steps-body">
              make a commit to an existing repository or open your own
            </span>
            <Link to="/dashboard" className="btn btn-primary welcome-next-steps-btn">
              Dashboard
            </Link>
          </li>
        </ul>

        <div className="founders-page-info">
          <h3 className="welcome-where-founders-heading">Founders Program</h3>
          <p className="founders-page-info-lead">
            Founders are the first 100 contributors who help seed the archive with real electronics
            repair documentation.
          </p>
          <div className="founders-recognition-highlight">
            <h3 className="founders-lifetime-benefits-heading">
              Lifetime Benefits
              <span className="founders-lifetime-benefits-divider" aria-hidden="true">
                ·
              </span>
              <span className="founders-lifetime-benefits-note">One approved contribution can qualify</span>
            </h3>
            <p className="founders-recognition-subtitle">
              Founding contributors receive visible, permanent reputation signals across the platform.
            </p>
            <div className="founders-benefits-badge-demo">
              <p className="founders-benefits-badge-demo-caption">Example next to your name on profiles and posts</p>
              <div className="founders-benefits-badge-demo-row">
                <span className="founders-benefits-badge-demo-name">username</span>
                <span className="founders-benefits-badge-demo-badges">
                  <CertifiedBadge
                    user={{ is_founding_contributor: true, forum_certified: true }}
                    title="Founding contributor and verified (example)"
                  />
                </span>
              </div>
            </div>
            <div className="founders-recognition-grid">
              <div className="founders-recognition-card">
                <h4>Wall Placement</h4>
                <p>Your profile is listed on the Wall of Founders for the launch cohort.</p>
              </div>
              <div className="founders-recognition-card">
                <h4>Founding Profile Badge</h4>
                <p>A founder badge appears on your public profile to mark early contribution.</p>
              </div>
              <div className="founders-recognition-card">
                <h4>Automatic Verified Repos Status</h4>
                <p>
                  Your repositories receive verified status automatically as part of founder recognition.{' '}
                  <Link to="/docs#verified-users" className="founders-recognition-doc-link">
                    More about verification
                  </Link>
                </p>
              </div>
            </div>
            <div className="founders-qualifications">
              <h4>Doing one of the following will activate your founding member status</h4>
              <ul>
                <li>New commit</li>
                <li>Circuit notes</li>
                <li>New repository</li>
                <li>New issue</li>
                <li>New release</li>
                <li>Correction</li>
              </ul>
            </div>
          </div>
          <p className="founders-page-info-note">
            Legal, provenance, and moderation policy remain in <Link to="/docs">Docs</Link>.
          </p>
        </div>
      </section>
    </div>
  )
}

export default Welcome
